import type { ReactNode } from 'react'
import { Prose } from '@/components/Prose'
import { KonepellinAlle } from '@/components/KonepellinAlle'
import { Chip } from '@/components/Chip'
import { LawBadge, DesignBadge, DivergenceBadge, AutomationDot } from '@/components/honesty'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { t } from '@/lib/ui'

// Sample styled subpage (WP-E1 task 5) — the STYLE REFERENCE. Renders the
// narrative spec NAR-002 (verbatim, English by design per DEC-002) as a reading
// experience with the two-truths overlay applied to real sentences. WP-F5
// replaces this with the full /tarkoitus area, sourcing the text from the
// compiled vault (WP-E2) rather than the inline copy below.
//
// The honesty traces here are grounded but illustrative (the page says so):
// they demonstrate the marks in the flow of reading, which is the whole point
// of the exercise (SIM-014/015).

interface Para {
  /** Verbatim NAR-002 paragraph (markdown — keeps the *italic* emphasis). */
  text: string
  /** Honesty marks that annotate a claim in this paragraph. */
  marks?: ReactNode
}

const paragraphs: Para[] = [
  {
    text: "I don't have to understand social security. That's the promise underneath all the others.",
  },
  {
    text: 'When my life changes — a child is born, a diagnosis lands, a job ends, I start studying, my rent outgrows my income — the law already says what I\'m entitled to. ArkkiKELA\'s job is to make that sentence true *for me, in my situation*, without my having to become an expert in it. If the state already knows something — my wages are in the incomes register, my family in the population register, my studies in KOSKI, my doctor\'s certificate in Kanta — I am not asked to prove it again. I\'m asked only for what nobody else can know.',
    marks: (
      <>
        <LawBadge statute="Perustuslaki 19 § — oikeus sosiaaliturvaan" specId="int-002" />
        <DesignBadge
          rationale="Rekisterilähtöinen kertakysyminen: tietoa ei pyydetä uudelleen, jos se on jo viranomaisrekisterissä."
          pattern="once-only (EU SDG 2018/1724)"
          specId="int-004"
        />
      </>
    ),
  },
  {
    text: 'I hear back quickly, and I know in advance how quickly. If my need is urgent and last-resort, the law gives my wait a hard ceiling counted in days, not intentions.',
    marks: <LawBadge statute="Laki toimeentulotuesta — käsittelyn määräaika" specId="ben-041" />,
  },
  {
    text: 'When the decision comes, I can read *why*. Not a form code — the actual reasons: what facts were used, which rule applied, what the amount rests on, and exactly how to object if I think it\'s wrong. If a machine made the decision, I\'m told so, and a human is one free request away. Disagreeing is not a confrontation; it\'s a right with an address, a deadline, and an independent board at the end of it.',
    marks: (
      <AutomationDot
        level="auto"
        statute="Hallintolaki 53 g § — seloste automaattisesta päätöksestä"
        humanRoute="Hallintolaki 53 f §: oikeus vaatia asian käsittelyä ihmisen toimesta, maksutta."
        specId="ai-001"
        showLabel
      />
    ),
  },
  {
    text: 'If my circumstances change, the recalculation happens when the change happens. I should never learn a year later that I owe money because a system looked backward instead of listening.',
    marks: (
      <DesignBadge
        rationale="Tapahtumapohjainen uudelleenarviointi taannehtivan vuositarkistuksen sijaan — vähentää takaisinperintää."
        pattern="event-driven reassessment"
        specId="prc-002"
      />
    ),
  },
  {
    text: 'And if I didn\'t know to ask — the promise still holds. Informing me is written into the institution\'s tasks. The ideal ArkkiKELA reaches me before I reach it.',
    marks: (
      <DivergenceBadge
        note="Proaktiivinen tavoittaminen ylittää nykyisen lakisääteisen vähimmäistason (NAR-002:n oma avoin kysymys)."
        divergenceId="A1"
        specId="int-002"
      />
    ),
  },
]

export function TarinaPage() {
  useDocumentTitle(t('tarina.title'))

  return (
    <article className="mx-auto max-w-shell px-4 py-12">
      <div className="max-w-prose">
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted">
          {t('tarina.kicker')}
        </p>
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
          {t('tarina.title')}
        </h1>

        <div className="mt-4 rounded border border-hairline bg-accent-soft/50 p-4 text-sm text-muted">
          {t('tarina.note')}
        </div>

        <div className="mt-8 space-y-6">
          {paragraphs.map((para, i) => (
            <div key={i}>
              <Prose variant="narrative">{para.text}</Prose>
              {para.marks && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="text-xs uppercase tracking-wide text-muted">
                    {t('tarina.marksLabel')}
                  </span>
                  {para.marks}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-hairline pt-4">
          <p className="text-sm text-muted">
            <span className="font-medium text-ink">{t('tarina.sourceLead')}:</span> NAR-002 ·{' '}
            <Chip variant="outline">narrative</Chip>
          </p>
          <KonepellinAlle specId="nar-002" />
        </div>
      </div>
    </article>
  )
}
